import React from 'react';

export interface StatCard {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  bgColor?: string;
  textColor?: string;
  change?: {
    value: number;
    trend: 'up' | 'down' | 'neutral';
    period?: string;
  };
}

interface StatsCardsProps {
  cards: StatCard[];
  loading?: boolean;
  className?: string;
  columns?: 1 | 2 | 3 | 4 | 5 | 6;
  /** Number of skeleton cards when loading. Defaults to columns. Use when card count > columns (e.g. 8 cards, 4 columns). */
  loadingRows?: number;
}

const StatsCards: React.FC<StatsCardsProps> = ({
  cards,
  loading = false,
  className = '',
  columns = 4,
  loadingRows
}) => {
  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
    5: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-5',
    6: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-6'
  };

  const skeletonCount = loadingRows ?? columns;

  if (loading) {
    return (
      <div className={`grid ${gridCols[columns]} gap-4 mb-6 ${className}`}>
        {[...Array(skeletonCount)].map((_, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm border p-6">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
              {index % 2 === 0 && <div className="h-3 bg-gray-200 rounded w-2/3 mt-2"></div>}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const getTrendIcon = (trend: 'up' | 'down' | 'neutral') => {
    switch (trend) {
    case 'up':
      return (
        <svg className="w-4 h-4 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      );
    case 'down':
      return (
        <svg className="w-4 h-4 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
        </svg>
      );
    case 'neutral':
      return (
        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h8" />
        </svg>
      );
    }
  };

  const getTrendColorClass = (trend: 'up' | 'down' | 'neutral') => {
    switch (trend) {
    case 'up':
      return 'text-success';
    case 'down':
      return 'text-error';
    case 'neutral':
      return 'text-gray-600';
    }
  };

  return (
    <div className={`grid ${gridCols[columns]} gap-4 mb-6 ${className}`}>
      {cards.map((card, index) => (
        <div key={index} className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className={`flex-shrink-0 ${card.bgColor || 'bg-primary/10'} rounded-md p-3`}>
              {card.icon}
            </div>
            <div className="ms-5 w-0 flex-1 min-w-0">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  {card.title}
                </dt>
                <dd className={`text-lg font-medium ${card.textColor || 'text-primary'}`}>
                  {typeof card.value === 'string' ? card.value : card.value.toLocaleString('en-US')}
                  {card.subtitle && (
                    <div className="text-sm text-gray-500 mt-1">
                      {card.subtitle}
                    </div>
                  )}
                  {card.change && (
                    <div className="flex items-center mt-2 text-sm gap-1">
                      {getTrendIcon(card.change.trend)}
                      <span className={getTrendColorClass(card.change.trend)}>
                        {card.change.value > 0 ? '+' : ''}{card.change.value}%
                      </span>
                      {card.change.period && (
                        <span className="text-gray-500">
                          {card.change.period}
                        </span>
                      )}
                    </div>
                  )}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default StatsCards;
